/**
 * CalendarModal Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Date range picker modal for property search. Allows users to select check-in and check-out dates
 * with visual calendar interface and European date format (DD/MM/YYYY).
 * 
 * Features:
 * - Month/year navigation
 * - Date range selection (start and end dates)
 * - Visual highlighting of selected range
 * - Disabled dates (past dates, unavailable dates)
 * - European date format (Monday start)
 * - Mobile-optimized modal
 * - Keyboard navigation support
 * - Clear and confirm buttons
 * 
 * Dependencies:
 * - React 19.2.4 (hooks, state)
 * - CustomCalendar (calendar grid)
 * - Font Awesome 6.5 (icons)
 * - TailwindCSS 4.1 (responsive styling)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with date range selection
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useModal } from '../contexts/ModalContext';
import { ProIcon } from './ProIcon';
import { CustomCalendar } from './CustomCalendar';
import { PortalModal } from './PortalModal';

interface CalendarModalProps {
  isOpen: boolean;
  startDate: Date | null;
  endDate: Date | null;
  onClose: () => void;
  onDatesChange: (dates: [Date | null, Date | null], flexible?: boolean, flexibleDays?: number) => void;
  initialFlexibleDays?: number;
  showFlexibleOption?: boolean;
}

export const CalendarModal: React.FC<CalendarModalProps> = ({
  isOpen,
  startDate,
  endDate,
  onClose,
  onDatesChange,
  initialFlexibleDays = 0,
  showFlexibleOption = true
}) => {
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(startDate);
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(endDate);
  const [isFlexible, setIsFlexible] = useState(initialFlexibleDays > 0);
  const [flexibleDays, setFlexibleDays] = useState(initialFlexibleDays || 2); // Default to 2 if not set
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(false);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const currentX = e.targetTouches[0].clientX;
    setTouchEnd(currentX);
    const distance = touchStart - currentX;
    // If horizontal movement detected, mark as swiping and prevent default
    if (Math.abs(distance) > 10) {
      setIsSwiping(true);
      e.preventDefault();
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    }
    if (isRightSwipe) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    }
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
  };

  const formatShortDate = (date: Date) =>
    date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    
    // Auto-set end date to +3 nights when only start date is selected
    if (start && !end && !selectingEnd) {
      const autoEndDate = new Date(start);
      autoEndDate.setDate(autoEndDate.getDate() + 3);
      setInternalStartDate(start);
      setInternalEndDate(autoEndDate);
      setSelectingEnd(true); // Keep end date selection active
    } else {
      setInternalStartDate(start);
      setInternalEndDate(end);
      if (end) {
        setSelectingEnd(false);
      }
    }
  };

  const handleApply = () => {
    onDatesChange([internalStartDate, internalEndDate], isFlexible, flexibleDays);
    onClose();
  };

  const handleClear = () => {
    setInternalStartDate(null);
    setInternalEndDate(null);
    setIsFlexible(false);
    setFlexibleDays(0);
    setSelectingEnd(false);
  };

  if (!isOpen) return null;

  return (
    <PortalModal isOpen={isOpen} onClose={onClose}>
      <div className="flex items-start justify-center min-h-screen pt-[10vh] pb-8 px-4">
        {/* Backdrop - clicking here closes the modal */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        
        {/* Modal content - clicks here don't close */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-white ui-radius-panel-top ui-modal-shell-radius ui-shadow-panel w-full max-w-4xl overflow-hidden z-10 flex flex-col max-h-[85vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <ProIcon icon="calendar-range" family="solid" className="w-5 h-5 text-brand-ocean" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">Choose Your Stay Dates</h3>
                <p className="text-xs text-slate-500">Pick check-in and check-out to see the best options</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="ui-btn-icon w-8 h-8 border-none"
            >
              <ProIcon icon="xmark" family="light" className="w-4 h-4 text-slate-600" />
            </button>
          </div>

          {/* Calendar */}
          <div 
            className="p-3 md:p-4 overflow-y-auto flex-1"
            style={{ touchAction: 'pan-y' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Calendar Grid - Single on mobile, double on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
              <CustomCalendar
                startDate={internalStartDate}
                endDate={internalEndDate}
                onDatesChange={handleDateChange}
                month={currentMonth}
                selectingEnd={selectingEnd}
                hoverDate={hoverDate}
                onHoverChange={setHoverDate}
              />
              <div className="hidden md:block">
                <CustomCalendar
                  startDate={internalStartDate}
                  endDate={internalEndDate}
                  onDatesChange={handleDateChange}
                  month={new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)}
                  selectingEnd={selectingEnd}
                  hoverDate={hoverDate}
                  onHoverChange={setHoverDate}
                />
              </div>
            </div>
            
            {/* Month Navigation */}
            <div className="flex items-center justify-center mt-4 gap-4">
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ProIcon icon="chevron-left" family="light" className="w-4 h-4 text-slate-600" />
              </button>
              <span className="text-sm font-medium text-slate-600">
                {isMobile
                  ? currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
                  : `${currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} - ${new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`}
              </span>
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ProIcon icon="chevron-right" family="light" className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            {/* Flexible Option - Only show for search, not booking */}
            {showFlexibleOption && (
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => {
                    setIsFlexible(!isFlexible);
                    if (!isFlexible) setFlexibleDays(2); // Default to 2 days when enabling
                  }}
                  className="flex items-center gap-3 group"
                >
                  <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${isFlexible ? 'bg-brand-sun border-brand-sun' : 'border-slate-300 group-hover:border-brand-sun'}`}>
                    {isFlexible && <ProIcon icon="circle-check" family="solid" className="text-xs text-white" />}
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-black text-slate-900">Flexible dates</div>
                    <div className="text-xs text-slate-500">Search within a range of dates</div>
                  </div>
                </button>
                
                {/* Flexible Days Options - Show on the right when enabled */}
                {isFlexible && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
                    <span className="text-xs font-black text-slate-900 sm:mr-2">How flexible?</span>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((days) => (
                        <button
                          key={days}
                          onClick={() => setFlexibleDays(days)}
                          className={`min-w-[44px] h-10 sm:w-8 sm:h-8 rounded-lg text-sm sm:text-xs font-black transition-all flex items-center justify-center ${
                            flexibleDays === days
                              ? 'bg-brand-sun text-white shadow-sm'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          ±{days}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Date Display */}
            <div className="flex items-center justify-between">
              <div className="text-xs">
                {internalStartDate && internalEndDate ? (
                  <div>
                    <p className="font-black text-slate-900">
                      {formatShortDate(internalStartDate)} - {formatShortDate(internalEndDate)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {internalStartDate && internalEndDate && 
                        `${Math.ceil((internalEndDate.getTime() - internalStartDate.getTime()) / (1000 * 60 * 60 * 24))} nights`
                      }
                      {selectingEnd && (
                        <span className="block text-brand-sun mt-1">Tap to change checkout date</span>
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-slate-500">Select check-in date</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleClear}
                  className="ui-btn-muted px-4 py-2 text-brand-ocean text-xs"
                >
                  Clear
                </button>
                <button
                  onClick={handleApply}
                  disabled={!internalStartDate || !internalEndDate}
                  className="ui-btn-primary px-6 py-2 text-xs disabled:opacity-50 disabled:cursor-not-allowed shadow-xl"
                >
                  Apply Selection
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </PortalModal>
  );
};
